import React, { useState, useCallback, useEffect } from 'react';
import PatientInfo from './PatientInfo';
import { useUltrasoundForm } from '../hooks/useUltrasoundForm';
import DraftRestoreBanner from './DraftRestoreBanner';
import { useTechnologists } from '../hooks/useTechnologists';

const UTERUS_POSITION = [
  'Anteverted',
  'Retroverted',
  'Midposition',
  'Not Well Visualized'
];

const UTERUS_ECHOTEXTURE = [
  'Normal',
  'Homogeneous',
  'Heterogeneous',
  'Fibroids',
  'Adenomyosis',
  'Not Well Visualized'
];

const FIBROID_LOCATION_TYPE = [
  'Intramural',
  'Submucosal',
  'Subserosal',
  'Pedunculated'
];

const FIBROID_DESCRIPTION = [
  'Typical fibroid',
  'Calcified fibroid',
  'Complex mass'
];

const ENDOMETRIUM_STATUS = [
  'Normal',
  'Thickened',
  'Thin',
  'Not Well Visualized',
  'Other Findings'
];

const OVARY_STATUS = [
  'Normal',
  'Not Visualized',
  'Oophorectomy',
  'CL/Follicle (<3cm)',
  'Polycystic'
];

function PelvicForm({ token, onBack, facilityInfo }) {
  // Tab state: 0=Uterus, 1=Endometrium & Cervix, 2=Right Ovary, 3=Left Ovary
  const [activeTab, setActiveTab] = useState(0);
  // Fetch dynamic technologists list 
    const { technologists, defaultTech } = useTechnologists();
  

  const INITIAL_PELVIC_DATA = {
    examType: 'Pelvic',
    examDate: new Date().toISOString().split('T')[0],
    firstName: '',
    lastName: '',
    mrn: '',
    technologist: '',
    reason: '',
    callReport: false,
    phoneNumber: '',
    history: '',
    premenopausal: false,
    perimenopausal: false,
    postmenopausal: false,
    hrt: false,
    iud: false,
    hysterectomy: false,
    supracervicalHysterectomy: false,
    comments: '',
    uterus: {
      position: 'Anteverted',
      echotexture: 'Normal',
      length: '',
      width: '',
      depth: '',
      other: '',
      masses: []
    },
    endometrium: {
      status: 'Normal',
      thickness: '',
      iudPosition: 'Expected position',
      other: ''
    },
    cervix: {
      status: 'Normal',
      other: ''
    },
    rightOvary: {
      status: ['Normal'],
      length: '',
      width: '',
      depth: '',
      other: '',
      masses: []
    },
    leftOvary: {
      status: ['Normal'],
      length: '',
      width: '',
      depth: '',
      other: '',
      masses: []
    },
    culDeSac: {
      fluid: false,
      other: ''
    },
    bladder: {
      status: 'Normal',
      other: ''
    }
  };

  const {
    formData,
    error,
    success,
    isLoading,
    handleSubmit: hookHandleSubmit,
    handlePreview,
    updateField,
    updateNestedField,
    resetForm,
    clearMessages,
    fieldErrors,
    draftBannerProps
  } = useUltrasoundForm(
    { ...INITIAL_PELVIC_DATA, technologist: defaultTech },
    'Pelvic',
    { token, facilityInfo }
  );

  // Either hysterectomy type removes the uterine body; only a full hysterectomy also removes the cervix
  const uterusRemoved = formData.hysterectomy || formData.supracervicalHysterectomy;

  useEffect(() => {
   if (defaultTech && !formData.technologist) {
     updateField('technologist', defaultTech);
   }  
  }, [defaultTech, formData.technologist]);

  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();
    await hookHandleSubmit(e);
  }, [hookHandleSubmit]);

  const handleEndometriumOtherChange = (value) => {
    const current = formData.endometrium;
    const newStatus = (value.trim() && current.status !== 'Other Findings')
      ? 'Other Findings'
      : current.status;
    updateField('endometrium', { ...current, other: value, status: newStatus });
  };

  const handleCervixOtherChange = (value) => {
    const current = formData.cervix;
    const newStatus = (value.trim() && current.status !== 'Other Findings')
      ? 'Other Findings'
      : current.status;
    updateField('cervix', { ...current, other: value, status: newStatus });
  };

  const handleOvaryStatusChange = (ovary, option) => {
    const ovaryKey = ovary === 'right' ? 'rightOvary' : 'leftOvary';
    const currentStatus = formData[ovaryKey].status;
    let newStatus;
    
    if (currentStatus.includes(option)) {
      newStatus = currentStatus.filter(s => s !== option);
      if (newStatus.length === 0) {
        newStatus = ['Normal'];
      }
    } else {
      if (option === 'Normal') {
        newStatus = [option];
      } else if (option === 'Not Visualized') {
        newStatus = [option];
      } else {
        newStatus = currentStatus.filter(s => s !== 'Normal' && s !== 'Not Visualized');
        newStatus.push(option);
      }
    }
    
    updateNestedField(`${ovaryKey}.status`, newStatus);
  };

  // Uterus mass functions
  const addUterusMass = () => {
    const newMass = {
      location: '',
      locationType: 'Intramural',
      size1: '',
      size2: '',
      size3: '',
      echotexture: 'Hypoechoic',
      description: 'Typical fibroid'
    };
    
    updateNestedField('uterus.masses', [...formData.uterus.masses, newMass]);
  };

  const removeUterusMass = (index) => {
    updateNestedField('uterus.masses', formData.uterus.masses.filter((_, i) => i !== index));
  };

  const handleUterusMassChange = (index, field, value) => {
    updateNestedField('uterus.masses', formData.uterus.masses.map((mass, i) => 
      i === index ? { ...mass, [field]: value } : mass
    ));
  };

  // Ovary mass functions
  const addOvaryMass = (ovary) => {
    const ovaryKey = ovary === 'right' ? 'rightOvary' : 'leftOvary';
    const newMass = {
      size1: '',
      size2: '',
      size3: '',
      echotexture: 'Cyst - Unilocular',
      description: ''
    };

    const currentOvary = formData[ovaryKey];
    updateField(ovaryKey, {
      ...currentOvary,
      masses: [...currentOvary.masses, newMass],
      status: currentOvary.status.filter(s => s !== 'Normal')
    });
  };

  const removeOvaryMass = (ovary, index) => {
    const ovaryKey = ovary === 'right' ? 'rightOvary' : 'leftOvary';
    updateNestedField(`${ovaryKey}.masses`, formData[ovaryKey].masses.filter((_, i) => i !== index));
  };

  const handleOvaryMassChange = (ovary, index, field, value) => {
    const ovaryKey = ovary === 'right' ? 'rightOvary' : 'leftOvary';
    updateNestedField(`${ovaryKey}.masses`, formData[ovaryKey].masses.map((mass, i) => 
      i === index ? { ...mass, [field]: value } : mass
    ));
  };

  return (
    <div className="form-container">
      <button onClick={onBack} className="back-btn" type="button">
        ← Back to Worksheet Selection
      </button>

      <form onSubmit={handleFormSubmit}>
        {/* Status Messages */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {success && (
          <div className="alert alert-success" role="alert">
            <strong>Success:</strong> {success}
            <button 
              type="button"
              className="btn-close" 
              onClick={clearMessages}
              aria-label="Close"
            />
          </div>
        )}

        {/* Draft restore banner */}
        {draftBannerProps && <DraftRestoreBanner {...draftBannerProps} />}

        {/* Patient Information */}
        <PatientInfo formData={formData} updateField={updateField} technologists={technologists} fieldErrors={fieldErrors} />

        {/* History */}
        <div className="form-section">
          <h3>History</h3>
          <div className="form-group">
            <label>Clinical History:</label>
            <input
              type="text"
              value={formData.history}
              onChange={(e) => updateField('history', e.target.value)}
              placeholder="Enter clinical history"
            />
          </div>
          
          <div className="checkbox-group checkbox-group-inline" style={{ marginTop: '15px' }}>
            <label>
              <input
                type="checkbox"
                checked={formData.premenopausal}
                onChange={(e) => updateField('premenopausal', e.target.checked)}
              />
              Premenopausal
            </label>
            <label>
              <input
                type="checkbox"
                checked={formData.perimenopausal}
                onChange={(e) => updateField('perimenopausal', e.target.checked)}
              />
              Perimenopausal
            </label>
            <label>
              <input
                type="checkbox"
                checked={formData.postmenopausal}
                onChange={(e) => updateField('postmenopausal', e.target.checked)}
              />
              Postmenopausal
            </label>
            <label>
              <input
                type="checkbox"
                checked={formData.hrt}
                onChange={(e) => updateField('hrt', e.target.checked)}
              />
              HRT
            </label>
            <label>
              <input
                type="checkbox"
                checked={formData.iud}
                onChange={(e) => updateField('iud', e.target.checked)}
              />
              IUD
            </label>
            <label>
              <input
                type="checkbox"
                checked={formData.hysterectomy}
                onChange={(e) => {
                  updateField('hysterectomy', e.target.checked);
                  if (e.target.checked) updateField('supracervicalHysterectomy', false);
                }}
              />
              Hysterectomy
            </label>
            <label>
              <input
                type="checkbox"
                checked={formData.supracervicalHysterectomy}
                onChange={(e) => {
                  updateField('supracervicalHysterectomy', e.target.checked);
                  if (e.target.checked) updateField('hysterectomy', false);
                }}
              />
              Supracervical Hysterectomy
            </label>
          </div>
        </div>

        {/* TAB NAVIGATION */}
        <div className="form-section">
          <div className="worksheet-tabs">
            <button
              type="button"
              onClick={() => setActiveTab(0)}
              className={`worksheet-tab-btn${activeTab === 0 ? ' active' : ''}`}
            >
              Uterus
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(1)}
              className={`worksheet-tab-btn${activeTab === 1 ? ' active' : ''}`}
            >
              Endometrium & Cervix
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(2)}
              className={`worksheet-tab-btn${activeTab === 2 ? ' active' : ''}`}
            >
              Right Ovary
            </button>
            <button
              type="button"
              onClick={() => setActiveTab(3)}
              className={`worksheet-tab-btn${activeTab === 3 ? ' active' : ''}`}
            >
              Left Ovary
            </button>
          </div>

          {/* Tab Content */}
          <div className="worksheet-tab-content" style={{ minHeight: '400px' }}>
            {/* Uterus Tab */}
            {activeTab === 0 && (
              <div className="tab-content-grid" style={{ opacity: uterusRemoved ? 0.5 : 1 }}>
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Uterus {uterusRemoved && <span style={{ color: 'var(--color-caution)', fontSize: '0.9em' }}>(Status post {formData.supracervicalHysterectomy ? 'supracervical ' : ''}hysterectomy)</span>}</h3>
          
          <div className="form-group">
            <label>Position:</label>
            <select
              value={formData.uterus.position}
              onChange={(e) => updateNestedField('uterus.position', e.target.value)}
              disabled={uterusRemoved}
            >
              {UTERUS_POSITION.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Echotexture:</label>
            <select
              value={formData.uterus.echotexture}
              onChange={(e) => updateNestedField('uterus.echotexture', e.target.value)}
              disabled={uterusRemoved}
            >
              {UTERUS_ECHOTEXTURE.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {formData.uterus.echotexture !== 'Not Well Visualized' && !uterusRemoved && (
            <div className="size-inputs">
              <label>Size (cm):</label>
              <input
                type="number"
                step="0.1"
                placeholder="Length"
                value={formData.uterus.length}
                onChange={(e) => updateNestedField('uterus.length', e.target.value)}
              />
              <span>×</span>
              <input
                type="number"
                step="0.1"
                placeholder="Width"
                value={formData.uterus.width}
                onChange={(e) => updateNestedField('uterus.width', e.target.value)}
              />
              <span>×</span>
              <input
                type="number"
                step="0.1"
                placeholder="Depth"
                value={formData.uterus.depth}
                onChange={(e) => updateNestedField('uterus.depth', e.target.value)}
              />
            </div>
          )}

          <div className="form-group">
            <label>Additional Findings:</label>
            <input
              type="text"
              value={formData.uterus.other}
              onChange={(e) => updateNestedField('uterus.other', e.target.value)}
              placeholder="Other observations"
              disabled={uterusRemoved}
            />
          </div>

          {!uterusRemoved && (
            <button type="button" onClick={addUterusMass} className="add-mass-btn">
              + Add Mass/Fibroid
            </button>
          )}

          {formData.uterus.masses.map((mass, index) => (
            <div key={index} className="mass-section">
              <h4>Uterine Mass/Fibroid {index + 1}</h4>
              
              <div className="form-group">
                <label>Location:</label>
                <input
                  type="text"
                  placeholder="e.g., Anterior wall, Fundal"
                  value={mass.location}
                  onChange={(e) => handleUterusMassChange(index, 'location', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Location Type:</label>
                <select
                  value={mass.locationType}
                  onChange={(e) => handleUterusMassChange(index, 'locationType', e.target.value)}
                >
                  {FIBROID_LOCATION_TYPE.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="size-inputs">
                <label>Size (cm):</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="L"
                  value={mass.size1}
                  onChange={(e) => handleUterusMassChange(index, 'size1', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="W"
                  value={mass.size2}
                  onChange={(e) => handleUterusMassChange(index, 'size2', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="H"
                  value={mass.size3}
                  onChange={(e) => handleUterusMassChange(index, 'size3', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Echotexture:</label>
                <select
                  value={mass.echotexture}
                  onChange={(e) => handleUterusMassChange(index, 'echotexture', e.target.value)}
                >
                  <option value="Hypoechoic">Hypoechoic</option>
                  <option value="Hyperechoic">Hyperechoic</option>
                  <option value="Complex">Complex</option>
                  <option value="Calcified">Calcified</option>
                </select>
              </div>

              <div className="form-group">
                <label>Description:</label>
                <select
                  value={mass.description}
                  onChange={(e) => handleUterusMassChange(index, 'description', e.target.value)}
                >
                  {FIBROID_DESCRIPTION.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <button type="button" onClick={() => removeUterusMass(index)} className="remove-btn">
                Remove Mass
              </button>
            </div>
          ))}
              </div>
            )}

            {/* Endometrium & Cervix Tab */}
            {activeTab === 1 && (
              <div className="tab-content-grid" style={{ opacity: formData.hysterectomy ? 0.5 : 1 }}>
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Endometrium {uterusRemoved && <span style={{ color: 'var(--color-caution)', fontSize: '0.9em' }}>(Status post {formData.supracervicalHysterectomy ? 'supracervical ' : ''}hysterectomy)</span>}</h3>
          
          <div className="form-group">
            <label>Status:</label>
            <select
              value={formData.endometrium.status}
              onChange={(e) => updateNestedField('endometrium.status', e.target.value)}
              disabled={uterusRemoved}
            >
              {ENDOMETRIUM_STATUS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {formData.endometrium.status !== 'Not Well Visualized' && !uterusRemoved && (
            <div className="form-group">
              <label>Endometrial Thickness (mm):</label>
              <input
                type="number"
                step="0.1"
                placeholder="Measurement"
                value={formData.endometrium.thickness}
                onChange={(e) => updateNestedField('endometrium.thickness', e.target.value)}
              />
            </div>
          )}

          {formData.iud && !uterusRemoved && (
            <div className="form-group">
              <label>IUD:</label>
              <select
                value={formData.endometrium.iudPosition}
                onChange={(e) => updateNestedField('endometrium.iudPosition', e.target.value)}
              >
                <option value="Expected position">Expected position</option>
                <option value="Unexpected position">Unexpected position</option>
                <option value="Not visualized">Not visualized</option>
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Additional Findings:</label>
            <input
              type="text"
              value={formData.endometrium.other}
              onChange={(e) => handleEndometriumOtherChange(e.target.value)}
              placeholder="Other observations"
              disabled={uterusRemoved}
            />
          </div>

                {/* Cervix */}
                <h4 style={{ marginTop: '30px' }}>Cervix {formData.hysterectomy && <span style={{ color: 'var(--color-caution)', fontSize: '0.9em' }}>(Status post hysterectomy)</span>}</h4>
          
          <div className="form-group">
            <label>Status:</label>
            <select
              value={formData.cervix.status}
              onChange={(e) => updateNestedField('cervix.status', e.target.value)}
              disabled={formData.hysterectomy}
            >
              <option value="Normal">Normal</option>
              <option value="Nabothian Cyst(s)">Nabothian Cyst(s)</option>
              <option value="Other Findings">Other Findings</option>
              <option value="Not Well Visualized">Not Well Visualized</option>
            </select>
          </div>

          <div className="form-group">
            <label>Additional Findings:</label>
            <input
              type="text"
              value={formData.cervix.other}
              onChange={(e) => handleCervixOtherChange(e.target.value)}
              placeholder="Other observations"
              disabled={formData.hysterectomy}
            />
          </div>
              </div>
            )}

            {/* Right Ovary Tab */}
            {activeTab === 2 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Right Ovary</h3>
          
          <div className="form-group">
            <label>Status (select all that apply):</label>
            <div className="checkbox-group">
              {OVARY_STATUS.map(option => (
                <label key={option} style={{ 
                  opacity: (formData.rightOvary.status.includes('Not Visualized') && option !== 'Not Visualized') || 
                           (formData.rightOvary.masses.length > 0 && option === 'Normal') ? 0.5 : 1 
                }}>
                  <input
                    type="checkbox"
                    checked={formData.rightOvary.status.includes(option)}
                    onChange={() => handleOvaryStatusChange('right', option)}
                    disabled={(formData.rightOvary.status.includes('Not Visualized') && option !== 'Not Visualized') ||
                             (formData.rightOvary.masses.length > 0 && option === 'Normal')}
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          {!formData.rightOvary.status.includes('Not Visualized') && !formData.rightOvary.status.includes('Oophorectomy') && (
            <div className="size-inputs">
              <label>Size (cm):</label>
              <input
                type="number"
                step="0.1"
                placeholder="Length"
                value={formData.rightOvary.length}
                onChange={(e) => updateNestedField('rightOvary.length', e.target.value)}
              />
              <span>×</span>
              <input
                type="number"
                step="0.1"
                placeholder="Width"
                value={formData.rightOvary.width}
                onChange={(e) => updateNestedField('rightOvary.width', e.target.value)}
              />
              <span>×</span>
              <input
                type="number"
                step="0.1"
                placeholder="Depth"
                value={formData.rightOvary.depth}
                onChange={(e) => updateNestedField('rightOvary.depth', e.target.value)}
              />
              {formData.rightOvary.length && formData.rightOvary.width && formData.rightOvary.depth && (
                <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>
                  = {(formData.rightOvary.length * formData.rightOvary.width * formData.rightOvary.depth * 0.52).toFixed(1)} cc
                </span>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Additional Findings:</label>
            <input
              type="text"
              value={formData.rightOvary.other}
              onChange={(e) => updateNestedField('rightOvary.other', e.target.value)}
              placeholder="Other observations"
            />
          </div>

          <button type="button" onClick={() => addOvaryMass('right')} className="add-mass-btn">
            + Add Cyst/Mass
          </button>

          {formData.rightOvary.masses.map((mass, index) => (
            <div key={index} className="mass-section">
              <h4>Right Ovary Cyst/Mass {index + 1}</h4>
              
              <div className="size-inputs">
                <label>Size (cm):</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="L"
                  value={mass.size1}
                  onChange={(e) => handleOvaryMassChange('right', index, 'size1', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="W"
                  value={mass.size2}
                  onChange={(e) => handleOvaryMassChange('right', index, 'size2', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="H"
                  value={mass.size3}
                  onChange={(e) => handleOvaryMassChange('right', index, 'size3', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Echotexture:</label>
                <select
                  value={mass.echotexture}
                  onChange={(e) => handleOvaryMassChange('right', index, 'echotexture', e.target.value)}
                >
                  <option value="Cyst - Unilocular">Cyst - Unilocular</option>
                  <option value="Cyst - Multilocular">Cyst - Multilocular</option>
                  <option value="Hemorrhagic Cyst">Hemorrhagic Cyst</option>
                  <option value="Endometrioma">Endometrioma</option>
                  <option value="Dermoid Cyst">Dermoid Cyst</option>
                  <option value="Hydrosalpinx">Hydrosalpinx</option>
                  <option value="Complex">Complex</option>
                  <option value="Solid">Solid</option>
                </select>
              </div>

              <div className="form-group">
                <label>Description:</label>
                <textarea
                  rows="2"
                  value={mass.description}
                  onChange={(e) => handleOvaryMassChange('right', index, 'description', e.target.value)}
                  placeholder="Additional details about the cyst/mass"
                  style={{ width: '100%', fontSize: '16px' }}
                />
              </div>

              <button type="button" onClick={() => removeOvaryMass('right', index)} className="remove-btn">
                Remove Cyst/Mass
              </button>
            </div>
          ))}
              </div>
            )}

            {/* Left Ovary Tab */}
            {activeTab === 3 && (
              <div className="tab-content-grid">
                <h3 style={{ color: 'var(--color-primary)', marginBottom: '20px' }}>Left Ovary</h3>
          
          <div className="form-group">
            <label>Status (select all that apply):</label>
            <div className="checkbox-group">
              {OVARY_STATUS.map(option => (
                <label key={option} style={{ 
                  opacity: (formData.leftOvary.status.includes('Not Visualized') && option !== 'Not Visualized') || 
                           (formData.leftOvary.masses.length > 0 && option === 'Normal') ? 0.5 : 1 
                }}>
                  <input
                    type="checkbox"
                    checked={formData.leftOvary.status.includes(option)}
                    onChange={() => handleOvaryStatusChange('left', option)}
                    disabled={(formData.leftOvary.status.includes('Not Visualized') && option !== 'Not Visualized') ||
                             (formData.leftOvary.masses.length > 0 && option === 'Normal')}
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>

          {!formData.leftOvary.status.includes('Not Visualized') && !formData.leftOvary.status.includes('Oophorectomy') && (
            <div className="size-inputs">
              <label>Size (cm):</label>
              <input
                type="number"
                step="0.1"
                placeholder="Length"
                value={formData.leftOvary.length}
                onChange={(e) => updateNestedField('leftOvary.length', e.target.value)}
              />
              <span>×</span>
              <input
                type="number"
                step="0.1"
                placeholder="Width"
                value={formData.leftOvary.width}
                onChange={(e) => updateNestedField('leftOvary.width', e.target.value)}
              />
              <span>×</span>
              <input
                type="number"
                step="0.1"
                placeholder="Depth"
                value={formData.leftOvary.depth}
                onChange={(e) => updateNestedField('leftOvary.depth', e.target.value)}
              />
              {formData.leftOvary.length && formData.leftOvary.width && formData.leftOvary.depth && (
                <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>
                  = {(formData.leftOvary.length * formData.leftOvary.width * formData.leftOvary.depth * 0.52).toFixed(1)} cc
                </span>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Additional Findings:</label>
            <input
              type="text"
              value={formData.leftOvary.other}
              onChange={(e) => updateNestedField('leftOvary.other', e.target.value)}
              placeholder="Other observations"
            />
          </div>

          <button type="button" onClick={() => addOvaryMass('left')} className="add-mass-btn">
            + Add Cyst/Mass
          </button>

          {formData.leftOvary.masses.map((mass, index) => (
            <div key={index} className="mass-section">
              <h4>Left Ovary Cyst/Mass {index + 1}</h4>
              
              <div className="size-inputs">
                <label>Size (cm):</label>
                <input
                  type="number"
                  step="0.1"
                  placeholder="L"
                  value={mass.size1}
                  onChange={(e) => handleOvaryMassChange('left', index, 'size1', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="W"
                  value={mass.size2}
                  onChange={(e) => handleOvaryMassChange('left', index, 'size2', e.target.value)}
                />
                <span>×</span>
                <input
                  type="number"
                  step="0.1"
                  placeholder="H"
                  value={mass.size3}
                  onChange={(e) => handleOvaryMassChange('left', index, 'size3', e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Echotexture:</label>
                <select
                  value={mass.echotexture}
                  onChange={(e) => handleOvaryMassChange('left', index, 'echotexture', e.target.value)}
                >
                  <option value="Cyst - Unilocular">Cyst - Unilocular</option>
                  <option value="Cyst - Multilocular">Cyst - Multilocular</option>
                  <option value="Hemorrhagic Cyst">Hemorrhagic Cyst</option>
                  <option value="Endometrioma">Endometrioma</option>
                  <option value="Dermoid Cyst">Dermoid Cyst</option>
                  <option value="Hydrosalpinx">Hydrosalpinx</option>
                  <option value="Complex">Complex</option>
                  <option value="Solid">Solid</option>
                </select>
              </div>

              <div className="form-group">
                <label>Description:</label>
                <textarea
                  rows="2"
                  value={mass.description}
                  onChange={(e) => handleOvaryMassChange('left', index, 'description', e.target.value)}
                  placeholder="Additional details about the cyst/mass"
                  style={{ width: '100%', fontSize: '16px' }}
                />
              </div>

              <button type="button" onClick={() => removeOvaryMass('left', index)} className="remove-btn">
                Remove Cyst/Mass
              </button>
            </div>
          ))}
              </div>
            )}
          </div>
        </div>

        {/* Cul-de-sac */}
        <div className="form-section">
          <h3>Cul-de-sac (Pouch of Douglas)</h3>
          
          <div style={{ marginBottom: '15px' }}>
            <label>
              <input
                type="checkbox"
                checked={formData.culDeSac.fluid}
                onChange={(e) => updateNestedField('culDeSac.fluid', e.target.checked)}
              />
              Free Fluid Present
            </label>
          </div>

          <div className="form-group">
            <label>Additional Findings:</label>
            <input
              type="text"
              value={formData.culDeSac.other}
              onChange={(e) => updateNestedField('culDeSac.other', e.target.value)}
              placeholder="Other observations"
            />
          </div>
        </div>

        {/* Bladder */}
        <div className="form-section">
          <h3>Bladder</h3>
          
          <div className="form-group">
            <label>Status:</label>
            <select
              value={formData.bladder.status}
              onChange={(e) => updateNestedField('bladder.status', e.target.value)}
            >
              <option value="Normal">Normal</option>
              <option value="Partially Distended">Partially Distended</option>
              <option value="Well Distended">Well Distended</option>
              <option value="Mass">Mass</option>
              <option value="Wall Thickening">Wall Thickening</option>
              <option value="Not Well Visualized">Not Well Visualized</option>
            </select>
          </div>

          <div className="form-group">
            <label>Additional Findings:</label>
            <input
              type="text"
              value={formData.bladder.other}
              onChange={(e) => updateNestedField('bladder.other', e.target.value)}
              placeholder="Other observations"
            />
          </div>
        </div>

        {/* Comments */}
        <div className="form-section">
          <h3>Comments</h3>
          <textarea
            rows="8"
            value={formData.comments}
            onChange={(e) => updateField('comments', e.target.value)}
            placeholder="Additional comments..."
            style={{ width: '100%', minHeight: '150px', fontSize: '16px' }}
          />
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            onClick={handlePreview}
            disabled={isLoading}
            className="preview-btn"
          >
            Preview Report
          </button>
          <button 
            type="submit" 
            disabled={isLoading} 
            className="submit-btn"
          >
            {isLoading ? 'Generating Report...' : 'Generate & Save Report'}
          </button>
          <button 
            type="button"
            onClick={resetForm}
            disabled={isLoading}
            className="reset-btn"
          >
            Clear Form
          </button>
        </div>
      </form>
    </div>
  );
}

export default PelvicForm;
